import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import {
   CambioUsuarioBase,
   CambioUsuarioBaseService,
   ContextoPlanCambioUsuarioBase,
   EmpresaCambioUsuarioBase,
   TerminoCondicionesCambioUsuario,
   UsuarioElegibleReemplazante,
} from "src/app/services/cambio-usuario-base.service";
import { MiCuentaService } from "src/app/services/mi-cuenta.service";
import { Response } from "src/app/shared/models/Response";
import { RutHelper } from "src/app/helpers/rut.helper";

type WizardStep =
   | "cargando"
   | "inicio"
   | "pendiente-en-curso"
   | "verificar"
   | "reemplazante"
   | "cupos"
   | "resumen"
   | "completado";

const CODIGO_LARGO = 6;
const DIGIT_ID_PREFIX = "cub-verif-digit-";

@Component({
   selector: "app-cambio-usuario-base-wizard",
   templateUrl: "./cambio-usuario-base-wizard.component.html",
   styleUrls: ["./cambio-usuario-base-wizard.component.scss"],
})
export class CambioUsuarioBaseWizardComponent implements OnInit, OnDestroy {
   @Output() completed = new EventEmitter<void>();
   @Output() closeRequested = new EventEmitter<void>();
   @Input() emailUsuarioBase = "";

   public step: WizardStep = "cargando";
   public loading = false;
   public processingAction = "";
   public errorMessage = "";
   public codigoReenviado = false;
   public vigenciaCodigoMinutos = 1;
   public segundosRestantesCodigo = 0;
   private expiracionCodigo: Date | null = null;
   private temporizadorCodigo: ReturnType<typeof setInterval> | null = null;

   public contexto: ContextoPlanCambioUsuarioBase | null = null;
   public cambio: CambioUsuarioBase | null = null;

   public codigoDigits: string[] = new Array(CODIGO_LARGO).fill("");

   public modoReemplazante: "existente" | "nuevo" = "existente";
   public usuarioReemplazanteId = "";
   public nuevoNombre = "";
   public nuevoApellidos = "";
   public nuevoRutFormateado = "";
   public nuevoEmail = "";

   public aceptaTerminos = false;
   public terminos: TerminoCondicionesCambioUsuario | null = null;
   public terminosError = "";

   public motivoCancelacion = "";

   public paginaEmpresas = 1;
   private readonly empresasPorPagina = 16;

   private readonly pasos: WizardStep[] = ["verificar", "reemplazante", "cupos", "resumen"];

   constructor(
      private cambioUsuarioBaseService: CambioUsuarioBaseService,
      private miCuentaService: MiCuentaService,
      private ref: ChangeDetectorRef
   ) {}

   ngOnInit(): void {
      this.cargarContexto();
      this.cargarTerminos();
      this.miCuentaService.obtenerVigenciaCodigo().subscribe({
         next: (resp) => {
            this.vigenciaCodigoMinutos = resp.data;
            if (this.step === "verificar" && this.cambio?.fechaSolicitud) {
               this.iniciarTemporizadorCodigo(new Date(this.cambio.fechaSolicitud));
            }
         },
      });
   }

   ngOnDestroy(): void {
      this.detenerTemporizadorCodigo();
   }

   get correoBaseEnmascarado(): string {
      const [usuario, dominio] = (this.emailUsuarioBase || "").trim().split("@");
      if (!usuario || !dominio) return "";
      const visible = usuario.length <= 3 ? usuario.slice(0, 1) : usuario.slice(0, 3);
      return `${visible}***@${dominio}`;
   }

   get tiempoRestanteCodigo(): string {
      const minutos = Math.floor(this.segundosRestantesCodigo / 60);
      const segundos = this.segundosRestantesCodigo % 60;
      return `${minutos.toString().padStart(2, "0")}:${segundos.toString().padStart(2, "0")}`;
   }

   private iniciarTemporizadorCodigo(inicio: Date = new Date()): void {
      this.detenerTemporizadorCodigo();
      this.expiracionCodigo = new Date(inicio.getTime() + this.vigenciaCodigoMinutos * 60 * 1000);
      this.actualizarSegundosRestantes();
      this.temporizadorCodigo = setInterval(() => {
         this.actualizarSegundosRestantes();
         if (this.segundosRestantesCodigo === 0) this.detenerTemporizadorCodigo();
         this.ref.detectChanges();
      }, 1000);
   }

   private actualizarSegundosRestantes(): void {
      this.segundosRestantesCodigo = this.expiracionCodigo
         ? Math.max(0, Math.ceil((this.expiracionCodigo.getTime() - Date.now()) / 1000))
         : 0;
   }

   private detenerTemporizadorCodigo(): void {
      if (this.temporizadorCodigo) clearInterval(this.temporizadorCodigo);
      this.temporizadorCodigo = null;
   }

   private cargarTerminos(): void {
      this.cambioUsuarioBaseService.obtenerTerminos().subscribe({
         next: (resp) => {
            this.terminos = resp.data;
            this.terminosError = "";
            this.ref.detectChanges();
         },
         error: (err) => {
            const response: Response<unknown> = err?.error;
            this.terminosError = response?.errors?.[0] || response?.message || "No fue posible cargar los términos y condiciones.";
            this.ref.detectChanges();
         },
      });
   }

   descargarTerminos(): void {
      this.cambioUsuarioBaseService.descargarTerminosPdf().subscribe({
         next: (resp) => {
            const blob = resp.body as Blob;
            const nombreArchivo = this.terminos?.nombreArchivo || "terminos-y-condiciones.pdf";
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = nombreArchivo;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
         },
         error: (err) => this.manejarError(err),
      });
   }

   get codigoVerificacion(): string {
      return this.codigoDigits.join("");
   }

   get codigoCompleto(): boolean {
      return this.codigoVerificacion.length === CODIGO_LARGO;
   }

   get empresasSinCupo(): EmpresaCambioUsuarioBase[] {
      return this.cambio?.empresas.filter((e) => !e.hayCupo) || [];
   }

   get pasoActualIndex(): number {
      if (this.step === "cupos" || (this.step === "resumen" && this.empresasSinCupo.length === 0)) {
         return this.step === "cupos" ? 2 : 3;
      }
      return this.pasos.indexOf(this.step);
   }

   get mostrarIndicadorPasos(): boolean {
      return ["verificar", "reemplazante", "cupos", "resumen"].includes(this.step);
   }

   private get empresasParaPaginar(): EmpresaCambioUsuarioBase[] {
      return this.cambio?.empresas || this.contexto?.empresas || [];
   }

   get totalPaginasEmpresas(): number {
      const total = this.empresasParaPaginar.length;
      return Math.max(1, Math.ceil(total / this.empresasPorPagina));
   }

   get indiceInicioPaginaEmpresas(): number {
      return (this.paginaEmpresas - 1) * this.empresasPorPagina;
   }

   get empresasPaginadas(): EmpresaCambioUsuarioBase[] {
      const inicio = this.indiceInicioPaginaEmpresas;
      return this.empresasParaPaginar.slice(inicio, inicio + this.empresasPorPagina);
   }

   get empresasColumnaIzquierda(): EmpresaCambioUsuarioBase[] {
      return this.empresasPaginadas.slice(0, 8);
   }

   get empresasColumnaDerecha(): EmpresaCambioUsuarioBase[] {
      return this.empresasPaginadas.slice(8, 16);
   }

   get hayColumnaDerecha(): boolean {
      return this.empresasColumnaDerecha.length > 0;
   }

   irAPaginaEmpresas(delta: number): void {
      const nueva = this.paginaEmpresas + delta;
      if (nueva >= 1 && nueva <= this.totalPaginasEmpresas) {
         this.paginaEmpresas = nueva;
      }
   }

   cargarContexto(): void {
      this.loading = true;
      this.errorMessage = "";
      this.cambioUsuarioBaseService.obtenerContexto().subscribe({
         next: (resp) => {
            this.loading = false;
            this.contexto = resp.data;
            this.paginaEmpresas = 1;

            const pendiente = resp.data.cambioPendiente;
            if (!pendiente) {
               this.step = "inicio";
               this.ref.detectChanges();
               return;
            }

            this.cambio = pendiente;
            this.step = this.determinarPasoDesdeCambio(pendiente);
            if (this.step === "verificar" && this.cambio?.fechaSolicitud) {
               this.iniciarTemporizadorCodigo(new Date(this.cambio.fechaSolicitud));
            }
            this.ref.detectChanges();
         },
         error: (err) => {
            this.manejarError(err);
         },
         complete: () => this.ref.detectChanges(),
      });
   }

   private determinarPasoDesdeCambio(cambio: CambioUsuarioBase): WizardStep {
      if (cambio.estadoCodigo === "PendienteActivacion") {
         return "pendiente-en-curso";
      }
      if (!cambio.emailVerificadoEn) {
         return "verificar";
      }
      if (!cambio.emailReemplazante) {
         return "reemplazante";
      }
      if (!cambio.todasConCupo) {
         return "cupos";
      }
      return "resumen";
   }

   iniciarCambio(): void {
      this.loading = true;
      this.errorMessage = "";
      this.cambioUsuarioBaseService.iniciar().subscribe({
         next: (resp) => {
            this.loading = false;
            this.cambio = resp.data;
            this.step = "verificar";
            this.iniciarTemporizadorCodigo(new Date(this.cambio.fechaSolicitud));
            this.ref.detectChanges();
         },
         error: (err) => this.manejarError(err),
      });
   }

   reenviarCodigo(): void {
      this.loading = true;
      this.processingAction = "reenviar";
      this.errorMessage = "";
      this.codigoReenviado = false;
      this.miCuentaService.sendEmailVerificationCode().subscribe({
         next: () => {
            this.loading = false;
            this.codigoReenviado = true;
            this.processingAction = "";
            this.codigoDigits = new Array(CODIGO_LARGO).fill("");
            this.iniciarTemporizadorCodigo();
            this.ref.detectChanges();
         },
         error: (err) => this.manejarError(err),
      });
   }

   confirmarCodigo(): void {
      if (!this.cambio || !this.codigoCompleto) {
         return;
      }

      this.loading = true;
      this.processingAction = "verificar";
      this.errorMessage = "";
      this.cambioUsuarioBaseService.confirmarVerificacionEmail(this.cambio.cambioUsuarioBaseId, this.codigoVerificacion).subscribe({
         next: () => {
            this.loading = false;
            if (this.cambio) {
               this.cambio.emailVerificadoEn = new Date().toISOString();
            }
            this.step = "reemplazante";
            this.ref.detectChanges();
         },
         error: (err) => this.manejarError(err),
      });
   }

   onDigitInput(index: number, event: any): void {
      const input = event.target as HTMLInputElement;
      const cleaned = (input.value || "").replace(/\D/g, "");

      if (cleaned.length > 1) {
         this.distribuirDigitos(index, cleaned);
         return;
      }

      this.codigoDigits[index] = cleaned;
      input.value = cleaned;
      if (cleaned && index < this.codigoDigits.length - 1) {
         setTimeout(() => this.enfocarDigito(index + 1));
      }
      this.ref.detectChanges();
   }

   onDigitPaste(index: number, event: ClipboardEvent): void {
      event.preventDefault();
      const clipboard = event.clipboardData || (window as any).clipboardData;
      const text = clipboard ? clipboard.getData("text") || "" : "";
      const digits = text.replace(/\D/g, "");
      if (digits) {
         this.distribuirDigitos(index, digits);
      }
   }

   onDigitKeydown(index: number, event: KeyboardEvent): void {
      const key = event.key;

      if (key === "Backspace") {
         event.preventDefault();
         if (this.codigoDigits[index]) {
            this.codigoDigits[index] = "";
            this.setInputValue(index, "");
         } else if (index > 0) {
            this.codigoDigits[index - 1] = "";
            this.setInputValue(index - 1, "");
            this.enfocarDigito(index - 1);
         }
         this.ref.detectChanges();
         return;
      }

      if (key === "ArrowLeft" && index > 0) {
         event.preventDefault();
         this.enfocarDigito(index - 1);
         return;
      }
      if (key === "ArrowRight" && index < this.codigoDigits.length - 1) {
         event.preventDefault();
         this.enfocarDigito(index + 1);
         return;
      }
      if (key === "Enter" && this.codigoCompleto) {
         event.preventDefault();
         this.confirmarCodigo();
      }
   }

   private distribuirDigitos(startIndex: number, digits: string): void {
      const total = this.codigoDigits.length;
      const useFromStart = digits.length >= total;
      const offset = useFromStart ? 0 : startIndex;
      const chars = digits.split("").slice(0, total - offset);

      if (useFromStart) {
         for (let i = 0; i < total; i++) {
            this.codigoDigits[i] = i < chars.length ? chars[i] : "";
         }
      } else {
         chars.forEach((ch, i) => (this.codigoDigits[offset + i] = ch));
      }

      const nextIndex = Math.min(offset + chars.length, total - 1);
      this.ref.detectChanges();
      setTimeout(() => {
         for (let i = 0; i < total; i++) {
            this.setInputValue(i, this.codigoDigits[i]);
         }
         this.enfocarDigito(nextIndex);
      });
   }

   private setInputValue(index: number, value: string): void {
      const el = document.getElementById(DIGIT_ID_PREFIX + index) as HTMLInputElement | null;
      if (el) {
         el.value = value;
      }
   }

   private enfocarDigito(index: number): void {
      const el = document.getElementById(DIGIT_ID_PREFIX + index) as HTMLInputElement | null;
      if (el) {
         el.focus();
         el.select();
      }
   }

   private avanzarSegunCupo(): void {
      this.step = this.cambio?.todasConCupo ? "resumen" : "cupos";
      this.paginaEmpresas = 1;
   }

   seleccionarExistente(): void {
      if (!this.cambio || !this.usuarioReemplazanteId) {
         return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.cambioUsuarioBaseService
         .seleccionarReemplazanteExistente(this.cambio.cambioUsuarioBaseId, this.usuarioReemplazanteId)
         .subscribe({
            next: (resp) => {
               this.loading = false;
               this.detenerTemporizadorCodigo();
               this.cambio = resp.data;
               this.avanzarSegunCupo();
               this.ref.detectChanges();
            },
            error: (err) => this.manejarError(err),
         });
   }

   onRutInput(event: any): void {
      const input = event.target as HTMLInputElement;
      const clean = RutHelper.clean(input.value)
         .replace(/[^0-9K]/g, "")
         .slice(0, 9);

      if (clean.length > 1) {
         const dv = clean.slice(-1);
         const mantisa = clean.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
         this.nuevoRutFormateado = `${mantisa}-${dv}`;
      } else {
         this.nuevoRutFormateado = clean;
      }

      input.value = this.nuevoRutFormateado;
   }

   seleccionarNuevo(): void {
      const rutParseado = RutHelper.parse(this.nuevoRutFormateado);
      if (!this.cambio || !rutParseado.rut || !this.nuevoNombre || !this.nuevoApellidos || !this.nuevoEmail) {
         this.errorMessage = "Debe completar rut, nombre, apellidos y correo del nuevo usuario.";
         return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.cambioUsuarioBaseService
         .seleccionarReemplazanteNuevo(this.cambio.cambioUsuarioBaseId, {
            rut: rutParseado.rut,
            nombre: this.nuevoNombre,
            apellidos: this.nuevoApellidos,
            email: this.nuevoEmail,
         })
         .subscribe({
            next: (resp) => {
               this.loading = false;
               this.cambio = resp.data;
               this.avanzarSegunCupo();
               this.ref.detectChanges();
            },
            error: (err) => this.manejarError(err),
         });
   }

   liberarCupo(empresa: EmpresaCambioUsuarioBase, usuario: UsuarioElegibleReemplazante): void {
      if (!this.cambio) {
         return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.cambioUsuarioBaseService.liberarCupo(this.cambio.cambioUsuarioBaseId, empresa.emisorId, usuario.usuarioId).subscribe({
         next: (resp) => {
            this.loading = false;
            this.cambio = resp.data;
            if (this.cambio.todasConCupo) {
               this.step = "resumen";
               this.paginaEmpresas = 1;
            }
            this.ref.detectChanges();
         },
         error: (err) => this.manejarError(err),
      });
   }

   confirmarDesactivacionBase(): void {
      if (!this.cambio) {
         return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.cambioUsuarioBaseService.liberarCupoBase(this.cambio.cambioUsuarioBaseId).subscribe({
         next: (resp) => {
            this.loading = false;
            this.cambio = resp.data;
            if (this.cambio.todasConCupo) {
               this.step = "resumen";
               this.paginaEmpresas = 1;
            }
            this.ref.detectChanges();
         },
         error: (err) => this.manejarError(err),
      });
   }

   confirmarTransferencia(): void {
      if (!this.cambio || !this.aceptaTerminos || !this.terminos) {
         return;
      }

      this.loading = true;
      this.processingAction = "confirmar";
      this.errorMessage = "";
      this.cambioUsuarioBaseService
         .confirmar(this.cambio.cambioUsuarioBaseId, this.terminos.versionTerminos, this.aceptaTerminos)
         .subscribe({
            next: () => {
               this.loading = false;
               this.step = "completado";
               this.completed.emit();
               this.ref.detectChanges();
            },
            error: (err) => this.manejarError(err),
         });
   }

   cancelarTransferencia(): void {
      if (!this.cambio) {
         this.closeRequested.emit();
         return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.cambioUsuarioBaseService.cancelar(this.cambio.cambioUsuarioBaseId, this.motivoCancelacion).subscribe({
         next: () => {
            this.loading = false;
            this.closeRequested.emit();
            this.ref.detectChanges();
         },
         error: (err) => this.manejarError(err),
      });
   }

   cerrar(): void {
      this.closeRequested.emit();
   }

   private manejarError(err: any): void {
      this.loading = false;
      this.processingAction = "";
      const response: Response<unknown> = err?.error;
      // errors[0] trae el motivo específico (ej. "rut ya existe"); message es solo un título genérico.
      this.errorMessage = response?.errors?.[0] || response?.message || "Ocurrió un error inesperado. Intente nuevamente.";
      this.ref.detectChanges();
   }
}
